import {Table} from '@chakra-ui/react'
import {flexRender, type Table as ITable} from '@tanstack/react-table'
import {EmptyStateProps, EmptyState} from './empty-state'

interface Props<DataRow> {
  table: ITable<DataRow>
  onRowClick?: (row: DataRow) => void
  emptyState?: EmptyStateProps
}

export function DataTable<DataRow>({table, onRowClick, emptyState}: Props<DataRow>) {
  const {rows} = table.getRowModel()

  return (
    <Table.Root>
      <Table.Header>
        {table.getHeaderGroups().map(headerGroup => (
          <Table.Row key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <Table.ColumnHeader
                key={header.id}
                style={{
                  width: '100%',
                  maxWidth: `${header.column.getSize()}%`,
                  minWidth: header.column.columnDef.minSize ? `${header.column.columnDef.minSize}px` : undefined,
                }}
              >
                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        ))}
      </Table.Header>

      <Table.Body userSelect="none">
        {rows.map(row => {
          const rowStyles = table.options.meta?.getRowStyles?.(row.original) ?? {}
          return (
            <Table.Row
              key={row.id}
              role={onRowClick ? 'button' : undefined}
              onClick={() => onRowClick?.(row.original)}
              _hover={onRowClick && {backgroundColor: 'gray.subtle', cursor: 'pointer'}}
              {...rowStyles}
            >
              {row.getVisibleCells().map(cell => {
                return (
                  <Table.Cell
                    key={cell.id}
                    style={{
                      width: '100%',
                      maxWidth: `${cell.column.getSize()}%`,
                      minWidth: cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                )
              })}
            </Table.Row>
          )
        })}

        {emptyState && rows.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={100}>
              <EmptyState {...emptyState} />
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  )
}
